/**
 * WeightedDotStrip (spec §5, with the owner's shape amendment) — the evidence
 * strip. Every comparable, one marker, on the SAME ruler as the ladder so
 * evidence and verdict share one axis.
 *
 * SOURCE IS ENCODED BY SHAPE, NOT COLOUR (the vendored module's documented
 * choice — it survives greyscale, colour blindness and a laptop angled across
 * a table). Colour additionally carries source, but shape is primary:
 *
 *   ● filled circle  = verified sale (is_closed)
 *   ◆ diamond        = asking price held on WingCaster (internal)
 *   ○ hollow circle  = portal asking price
 *
 * A sale and an ask are different events; the marker shape plus the
 * "Sold …" / "First seen …" labelling (spec §11.5) keeps them distinct
 * visually AND textually.
 *
 * Marker size ∝ weight; legend once, top-inline-end of the strip. Keyboard:
 * every marker is focusable; screen readers get a visually-hidden table
 * alternative (a11y tier requirement).
 */

import type { KeyboardEvent } from 'react'
import type { SubjectComparable } from '@/types/marketPricing'
import { formatExact, formatMoney } from './pricingIntelligence'
import {
  formatObservedDate,
  priceBriefT,
  type PriceBriefLocale,
} from './copy'
import { rulerGeometry, rulerX, type RulerDirection } from './priceScale'

const WIDTH = 600
const HEIGHT = 84
const PAD = 46
const BASE_Y = 44

type EvidenceShape = 'filled-circle' | 'hollow-circle' | 'diamond'

interface ClassifiedComparable {
  comparable: SubjectComparable
  shape: EvidenceShape
  radius: number
  x: number
}

function evidenceShapeFor(comparable: SubjectComparable): EvidenceShape {
  if (comparable.is_closed) return 'filled-circle'
  const code = String(comparable.source || '').toLowerCase()
  if (code === 'internal' || code.includes('wingcaster')) return 'diamond'
  return 'hollow-circle'
}

function shapeFill(shape: EvidenceShape): string {
  // Colour is SECONDARY to shape — the same information, redundantly.
  if (shape === 'filled-circle') return 'var(--lc-text-brand)'
  if (shape === 'diamond') return 'var(--lc-accent-bold)'
  return 'var(--lc-surface)'
}

function shapeStroke(shape: EvidenceShape): string {
  if (shape === 'hollow-circle') return 'var(--lc-text-muted)'
  return 'none'
}

/** Radius ∝ weight, 5–11px, so resemblance visibly sizes the evidence. */
function radiusForWeight(weight: number, min: number, max: number): number {
  const w = Number.isFinite(weight) && weight > 0 ? weight : 0.0001
  const span = max - min
  const t = span > 0 ? Math.min(1, Math.max(0, (w - min) / span)) : 0.5
  return 5 + 6 * t
}

function classifyComparables(
  comparables: SubjectComparable[],
  domain: { min: number; max: number },
  dir: RulerDirection,
): ClassifiedComparable[] {
  const geom = rulerGeometry(domain, WIDTH, PAD)
  const weights = comparables.map((c) => (Number(c.weight) > 0 ? Number(c.weight) : 0.0001))
  const minW = Math.min(...weights)
  const maxW = Math.max(...weights)
  return comparables.map((comparable, index) => ({
    comparable,
    shape: evidenceShapeFor(comparable),
    radius: radiusForWeight(weights[index], minW, maxW),
    x: rulerX(geom, Number(comparable.normalized_price) || 0, dir),
  }))
}

function markerAriaLabel(item: ClassifiedComparable, locale: PriceBriefLocale, currency: string): string {
  const c = item.comparable
  const kind = c.is_closed ? priceBriefT('kindVerifiedSale', locale) : priceBriefT('kindAsking', locale)
  const observed = c.is_closed
    ? priceBriefT('observedSold', locale, { date: formatObservedDate(c.observed_at) })
    : priceBriefT('observedFirstSeen', locale, { date: formatObservedDate(c.observed_at) })
  return `${kind}, ${formatMoney(c.normalized_price, currency)}, ${c.source_label || c.source}, ${observed}`
}

interface WeightedDotStripProps {
  comparables: SubjectComparable[]
  domain: { min: number; max: number }
  currency: string
  dir: RulerDirection
  locale: PriceBriefLocale
  selectedId: string | null
  onSelect: (id: string | null) => void
}

export function WeightedDotStrip({
  comparables,
  domain,
  currency,
  dir,
  locale,
  selectedId,
  onSelect,
}: WeightedDotStripProps) {
  const items = classifyComparables(comparables, domain, dir)
  const geom = rulerGeometry(domain, WIDTH, PAD)
  const baselineX0 = dir === 'rtl' ? geom.x1 : geom.x0
  const baselineX1 = dir === 'rtl' ? geom.x0 : geom.x1

  const legend: { shape: EvidenceShape; label: string }[] = [
    { shape: 'filled-circle', label: priceBriefT('legendVerifiedSale', locale) },
    { shape: 'diamond', label: priceBriefT('legendInternal', locale) },
    { shape: 'hollow-circle', label: priceBriefT('legendPortal', locale) },
  ]

  const onMarkerKeyDown = (event: KeyboardEvent<SVGGElement>, id: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onSelect(id)
    }
  }

  return (
    <div className="relative" data-testid="evidence-strip" data-dir={dir}>
      {/* Legend once, top-inline-end (mirrors with direction). */}
      <ul
        className="absolute top-0 m-0 flex list-none gap-[var(--lc-space-md)] p-0"
        style={{ insetInlineEnd: 0 }}
        data-testid="evidence-legend"
        aria-label={priceBriefT('evidenceEyebrow', locale)}
      >
        {legend.map((entry) => (
          <li key={entry.shape} className="flex items-center gap-[var(--lc-space-2xs)]">
            <svg width={14} height={14} viewBox="-7 -7 14 14" aria-hidden="true">
              <EvidenceGlyph shape={entry.shape} radius={4.5} />
            </svg>
            <span className="text-[length:var(--lc-type-caption)] text-[var(--lc-text-muted)]">
              {entry.label}
            </span>
          </li>
        ))}
      </ul>

      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="mt-[var(--lc-space-xl)] block w-full" role="group"
        aria-label={priceBriefT('evidenceCount', locale, { count: comparables.length })}
      >
        {/* Shared ruler baseline. */}
        <line
          x1={baselineX0}
          y1={BASE_Y}
          x2={baselineX1}
          y2={BASE_Y}
          stroke="var(--lc-border-strong)"
          strokeWidth={1.5}
        />
        {items.map((item) => {
          const selected = item.comparable.id === selectedId
          return (
            <g
              key={item.comparable.id}
              role="button"
              tabIndex={0}
              data-testid={`evidence-marker-${item.comparable.id}`}
              data-shape={item.shape}
              data-x={Math.round(item.x)}
              aria-pressed={selected}
              aria-label={markerAriaLabel(item, locale, currency)}
              className="evidence-marker cursor-pointer"
              onClick={() => onSelect(selected ? null : item.comparable.id)}
              onKeyDown={(event) => onMarkerKeyDown(event, item.comparable.id)}
            >
              {/* Invisible fat hit target around the mark. */}
              <circle cx={item.x} cy={BASE_Y} r={Math.max(14, item.radius + 6)} fill="transparent" />
              <EvidenceGlyph shape={item.shape} radius={item.radius} x={item.x} y={BASE_Y} selected={selected} />
            </g>
          )
        })}
      </svg>

      {/* Screen-reader table alternative — the same evidence, row by row. */}
      <table className="sr-only">
        <caption>{priceBriefT('srEvidenceTableCaption', locale)}</caption>
        <thead>
          <tr>
            <th scope="col">{priceBriefT('srKind', locale)}</th>
            <th scope="col">{priceBriefT('srPrice', locale)}</th>
            <th scope="col">{priceBriefT('srSource', locale)}</th>
            <th scope="col">{priceBriefT('srSize', locale)}</th>
            <th scope="col">{priceBriefT('srSimilarity', locale)}</th>
            <th scope="col">{priceBriefT('srObserved', locale)}</th>
          </tr>
        </thead>
        <tbody>
          {comparables.map((c) => (
            <tr key={c.id}>
              <td>{c.is_closed ? priceBriefT('kindVerifiedSale', locale) : priceBriefT('kindAsking', locale)}</td>
              <td>{formatExact(c.normalized_price, currency)}</td>
              <td>{c.source_label || c.source}</td>
              <td>{c.area_sqm != null ? priceBriefT('cardSize', locale, { value: c.area_sqm }) : '—'}</td>
              <td>
                {c.similarity_score != null
                  ? priceBriefT('cardSimilarity', locale, { pct: Math.round(c.similarity_score * 100) })
                  : '—'}
              </td>
              <td>
                {c.is_closed
                  ? priceBriefT('observedSold', locale, { date: formatObservedDate(c.observed_at) })
                  : priceBriefT('observedFirstSeen', locale, { date: formatObservedDate(c.observed_at) })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** The mark itself, drawn at (x, y) — at origin when used inside the legend. */
function EvidenceGlyph({
  shape,
  radius,
  x = 0,
  y = 0,
  selected = false,
}: {
  shape: EvidenceShape
  radius: number
  x?: number
  y?: number
  selected?: boolean
}) {
  const stroke = selected ? 'var(--lc-focus-ring)' : shapeStroke(shape)
  const strokeWidth = selected ? 2.5 : 1.75
  if (shape === 'diamond') {
    return (
      <path
        d={`M ${x} ${y - radius} L ${x + radius} ${y} L ${x} ${y + radius} L ${x - radius} ${y} Z`}
        fill={shapeFill(shape)}
        stroke={stroke}
        strokeWidth={stroke === 'none' ? 0 : strokeWidth}
      />
    )
  }
  return (
    <circle
      cx={x}
      cy={y}
      r={radius}
      fill={shapeFill(shape)}
      stroke={stroke}
      strokeWidth={stroke === 'none' ? 0 : strokeWidth}
    />
  )
}
